import { Injectable } from '@angular/core';
import { MapperFactory } from '@products/utils/mappers/mapper-factory';
import { MapperParams } from '@products/utils/mappers/type.mapper';
import { Logger } from '@utils/logger';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { GermanCalculatorResultDTO } from '../types/dto/german-calculator.dto.types';
import { GermanPensionCalculatorResultDTO } from '../types/dto/pension-calculator.dto.types';
import { GermanPensionEstimatorResultDTO } from '../types/dto/pension-estimator.dto.types';
import { ShortfallClosureResultDTO } from '../types/dto/shortfall-closure-calculator.dto.types';
import {
  GermanCalculatorAPIRequestParams,
  GermanCalculatorType,
  GermanPensionCalculatorFormBindValues,
} from '../types/german-calculator.type';
import { GermanCalculatorMapper } from '../types/mapper/german-calculator.mapper';
import { PensionCalculatorMapper } from '../types/mapper/pension-calculator.mapper';
import { PensionEstimatorMapper } from '../types/mapper/pension-estimator.mapper';
import { ShortfallClosureMapper } from '../types/mapper/shortfall-closure-calculator.mapper';
import { GermanCalculatorResult } from '../types/model/german-calculator';
import {
  PensionCalculatorResult,
  PensionEstimatorResult,
  ShortfallClosureResult,
} from '../types/model/pension-calculator';
import { GermanCalculatorAPIService } from './german-calculator-api.service';

const logger = Logger.getLogger('GermanCalculatorService');

@Injectable({
  providedIn: 'root',
})
export class GermanCalculatorService {
  constructor(
    private mapperFactory: MapperFactory,
    private gcApiService: GermanCalculatorAPIService
  ) {}

  /**
   * Formats the received response to required type.
   * @param requestParams calculator form field values as params
   */
  register(
    calculatorType: GermanCalculatorType,
    requestParams: GermanCalculatorAPIRequestParams
  ): Observable<GermanCalculatorResult> {
    return this.fetchResult(calculatorType, requestParams).pipe(
      map((rawResultDTO) => {
        logger.debug('Raw Result: ', rawResultDTO);

        const germanCalculatorMapperParams: MapperParams = {};

        const mappedGermanCalculatorResult: GermanCalculatorResult = rawResultDTO
          ? this.mapperFactory
              .createMapper(
                GermanCalculatorMapper,
                germanCalculatorMapperParams
              )
              .toDomain(rawResultDTO)
          : null;

        return mappedGermanCalculatorResult;
      })
    );
  }

  /**
   * Sends http request and receives calculated response.
   * @param requestParams calculator form field values as params
   */
  fetchResult(
    calculatorType: GermanCalculatorType,
    requestParams: GermanCalculatorAPIRequestParams
  ): Observable<GermanCalculatorResultDTO> {
    return this.gcApiService.getResult(calculatorType, requestParams);
  }

  /**
   * Formats the received response to required type for Pension Calculator.
   * @param requestParams calculator form field values as params
   */
  registerForPensionCalculator(
    calculatorType: GermanCalculatorType,
    requestParams: GermanPensionCalculatorFormBindValues
  ): Observable<PensionCalculatorResult> {
    return this.fetchResultPensionCalculator(
      calculatorType,
      requestParams
    ).pipe(
      map((rawResultDTO) => {
        logger.debug('Raw Result: ', rawResultDTO);

        const pensionCalculatorMapperParams: MapperParams = {};

        const mappedGermanCalculatorResult: PensionCalculatorResult = rawResultDTO
          ? this.mapperFactory
              .createMapper(
                PensionCalculatorMapper,
                pensionCalculatorMapperParams
              )
              .toDomain(rawResultDTO)
          : null;

        return mappedGermanCalculatorResult;
      })
    );
  }

  /**
   * Sends http request and receives calculated response.
   * @param requestParams calculator form field values as params
   */
  fetchResultPensionCalculator(
    calculatorType: GermanCalculatorType,
    requestParams: GermanPensionCalculatorFormBindValues
  ): Observable<GermanPensionCalculatorResultDTO> {
    return this.gcApiService.getResultPensionCalulator(
      calculatorType,
      requestParams
    );
  }

  /**
   * Formats the received response to required type for Pension Estimator.
   * @param requestParams calculator form field values as params
   */
  registerForPensionEstimator(
    calculatorType: GermanCalculatorType,
    requestParams: GermanPensionCalculatorFormBindValues
  ): Observable<PensionEstimatorResult> {
    return this.fetchResultPensionEstimator(calculatorType, requestParams).pipe(
      map((rawResultDTO) => {
        logger.debug('Raw Result: ', rawResultDTO);

        const germanCalculatorMapperParams: MapperParams = {};

        const mappedGermanCalculatorResult: PensionEstimatorResult = rawResultDTO
          ? this.mapperFactory
              .createMapper(
                PensionEstimatorMapper,
                germanCalculatorMapperParams
              )
              .toDomain(rawResultDTO)
          : null;

        return mappedGermanCalculatorResult;
      })
    );
  }

  /**
   * Sends http request and receives calculated response.
   * @param requestParams calculator form field values as params
   */
  fetchResultPensionEstimator(
    calculatorType: GermanCalculatorType,
    requestParams: GermanPensionCalculatorFormBindValues
  ): Observable<GermanPensionEstimatorResultDTO> {
    return this.gcApiService.getResultPensionEstimator(
      calculatorType,
      requestParams
    );
  }

  /**
   * Formats the received response to required type for Pension Estimator.
   * @param requestParams calculator form field values as params
   */
  registerForShortfallClosure(
    calculatorType: GermanCalculatorType,
    requestParams: GermanPensionCalculatorFormBindValues
  ): Observable<ShortfallClosureResult> {
    return this.fetchResultShortfallClosure(calculatorType, requestParams).pipe(
      map((rawResultDTO) => {
        logger.debug('Raw Result: ', rawResultDTO);

        const shortfallClosureMapperParams: MapperParams = {};

        const mappedGermanCalculatorResult: ShortfallClosureResult = rawResultDTO
          ? this.mapperFactory
              .createMapper(
                ShortfallClosureMapper,
                shortfallClosureMapperParams
              )
              .toDomain(rawResultDTO)
          : null;

        return mappedGermanCalculatorResult;
      })
    );
  }

  /**
   * Sends http request and receives calculated response.
   * @param requestParams calculator form field values as params
   */
  fetchResultShortfallClosure(
    calculatorType: GermanCalculatorType,
    requestParams: GermanPensionCalculatorFormBindValues
  ): Observable<ShortfallClosureResultDTO> {
    return this.gcApiService.getResultShortfallClosure(
      calculatorType,
      requestParams
    );
  }
}
